<template>
	<ProcessCreatePage>
		<Header :title="evaluateComprehensiveTitle">
			{{ $t('create_document_information') }}
		</Header>

		<div class="main">
			<YesNoSelect ref="yesNo" :required="true" :label="$t('expertise_exists')" v-model="isDamageExpertise" class="w-1/3" />
			<div v-if="isDamageExpertise != null && !isDamageExpertise.value">
				<YesNoSelect ref="yesNco" :required="true" :label="$t('cost_estimate_exists')" v-model="isExpertiseCalculation" class="w-1/3" />

				<div v-if="isExpertiseCalculation != null && isExpertiseCalculation.value" class="flex flex-wrap">
					<BaseInput
						class="w-full md:w-1/3 mr-6"
						ref="BaseSelect.repairShopCompanies"
						:label="$t('company')"
						v-model="expertiseCompany"
						options-label="display"
						track-by="value"
						:required="true"
						:multiple="false"
					/>
					<BaseDatepicker
						ref="BaseInput.date"
						class="w-full md:w-1/3"
						:required="true"
						:label="$t('date_cost_estimate')"
						v-model="expertiseDate"
						:max_date="new Date()"
						:min_date="getMinDate"
					/>
				</div>
			</div>
		</div>

		<Footer @continueButtonClick="continueButtonClick(false)" :is-skippable="false" :is-back-button="false" />
	</ProcessCreatePage>
</template>

<script>
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Footer from '@/components/damagecases/Footer';
import Header from '@/components/damagecases/Header';
import YesNoSelect from '@/components/forms/inputs/YesNoSelect';
import Validate from '@/mixins/Validate';
import ProcessCreatePage from '@/views/management/Process/ProcessCreatePage';
import { DamagecasesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker.vue';
import Authentication from '@/mixins/Authentication';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import LoadingSpinner from '@/mixins/LoadingSpinner';

export default {
	name: 'ProcessCreateComprehensiveDamageAssessor',
	components: { BaseInput, BaseDatepicker, YesNoSelect, ProcessCreatePage, Header, Footer },
	mixins: [AppContainerMixin, Validate, Authentication, LoadingSpinner],
	data() {
		return {
			isDamageExpertise: null,
			isExpertiseCalculation: null,
			expertiseDate: null,
			expertiseCompany: null,
			model: {
				city: {},
			},
			minDate: new Date(),
			process: null,
		};
	},
	computed: {
		getMinDate() {
			return new Date(this.$store.getters.getProcessCreateComprehensive.accident.date * 1000);
		},
		getCompanies() {
			return [
				{ display: this.getUserCompany.name, value: this.getUserCompany.name },
				{ display: '', value: 'other' },
			];
		},
		evaluateComprehensiveTitle() {
			return this.$store.getters.getProcessCreateComprehensive.insurance === null
				? this.$t('new_own_damage_expertise').toString()
				: this.$t('new_comprehensive_expertise').toString();
		},
		getComprehensiveType() {
			return this.$store.getters.getProcessCreateComprehensive.insurance === null
				? this.$t('own_damage').toString()
				: this.$t('comprehensive').toString();
		},
	},
	methods: {
		continueButtonClick: function(isSkip) {
			if (isSkip) {
				this.$emit('next');
			} else if (this.validate()) {
				if (this.isExpertiseCalculation != null && !this.isExpertiseCalculation.value) {
					this.$swal({
						title: this.$t('general.attention').toString(),
						text: this.$t('no_expertise_or_estimate_create_documents').toString(),
						icon: 'warning',
						cancelButtonText: this.$t('general.no').toString(),
						showCancelButton: true,
						confirmButtonText: this.$t('general.yes').toString(),
					}).then(result => {
						if (!result.value) {
							this.$router.push(this.$route.params.backlink);
						} else {
							this.setLoadingStatus(true);
							this.$store.commit('setProcessCreateComprehensiveAppraisal', null);
							this.$store.commit('setProcessCreateComprehensiveCostEstimation', null);
							DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
								.damagecasesComprehensivePost(this.$store.getters.getProcessCreateComprehensive)
								.then(response => {
									this.process = { ...this.$store.getters.getProcessCreateComprehensive }; // make a copy, because in finally it will be cleared
									this.process.id = response.data.data.id;
									this.process.imd_net = response.data.data.imdNetId;
								})
								.then(() => {
									this.setLoadingStatus(false);
									this.$swal({
										title: this.$t('general.success').toString(),
										text: this.$t('comprehensive_saved', { type: this.getComprehensiveType }).toString(),
										icon: 'success',
										confirmButtonText: this.$t('general.next').toString(),
										showCancelButton: false,
									}).then(result => {
										if (result.value) {
											this.$store.commit('clearProcessCreateComprehensive');
											this.$store.commit('setConfirmLeaving', false);
											this.$router.push({
												name: 'management.process.edit',
												params: { processId: this.process.id, customer: this.$route.query.customer_id || this.$route.query.customer },
											});
										}
									});
								})
								.catch(error => {
									this.setLoadingStatus(false);
									this.isError = true;
									this.$swal(this.$t('general.error').toString(), this.$t('comprehensive_saved_error').toString(), 'error');
								})
								.finally(() => (this.isLoading = false));
						}
					});
				} else {
					this.setLoadingStatus(true);
					this.$store.commit('setProcessCreateComprehensiveAppraisal', this.isDamageExpertise.value);
					this.$store.commit(
						'setProcessCreateComprehensiveCostEstimation',
						this.isExpertiseCalculation == null ? false : this.isExpertiseCalculation.value,
					);

					if (this.expertiseCompany != null) {
						this.$store.commit('setProcessCreateComprehensiveEstimationCompany', this.expertiseCompany);
						this.$store.commit('setProcessCreateComprehensiveDateCostEstimation', parseInt(this.expertiseDate.getTime() / 1000));
					}

					DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.damagecasesComprehensivePost(this.$store.getters.getProcessCreateComprehensive)
						.then(response => {
							this.process = { ...this.$store.getters.getProcessCreateComprehensive }; // make a copy, because in finally it will be cleared
							this.process.id = response.data.data.id;
							this.process.imd_net = response.data.data.imdNetId;
						})
						.then(() => {
							this.setLoadingStatus(false);
							this.$swal({
								title: this.$t('general.success').toString(),
								text:
									this.isExpertiseCalculation == null
										? this.$t('comprehensive_saved_expertise', { type: this.getComprehensiveType }).toString()
										: this.$t('comprehensive_saved_estimate', { type: this.getComprehensiveType }).toString(),
								icon: 'success',
								confirmButtonText: this.$t('general.next').toString(),
								showCancelButton: false,
							}).then(result => {
								if (result.value) {
									this.$store.commit('clearProcessCreateComprehensive');
									this.$store.commit('setConfirmLeaving', false);
									this.$router.push({
										name: 'management.process.edit',
										params: { processId: this.process.id, customer: this.$route.query.customer_id || this.$route.query.customer },
									});
								}
							});
						})
						.catch(error => {
							this.setLoadingStatus(false);
							this.isError = true;
							this.$swal(this.$t('general.error').toString(), this.$t('comprehensive_saved_error').toString(), 'error');
						})
						.finally(() => (this.isLoading = false));
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
	},
	watch: {
		isExpertiseCalculation: function(newValue) {
			if (newValue != null && this.expertiseCompany === null) {
				this.expertiseCompany = this.getUserCompany.name;
			}
		},
	},
};
</script>
